import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import { Divider } from "antd";
import { cloneDeep } from "lodash";
import moment from "moment";
import { Text, TooltipParagraph } from "@iiht/components";
import { useExtendVMValidity, useRateLab } from "../../../../../customHooks";
import {
  userNotificationUpdateReadStatus,
  getUserNotificationListData,
  setUserNotificationListData,
  getNotificationWebSocketTicket,
  setUserNotificationListDataWs,
} from "../../../../../store/actions";
import NotificationModal from "../../../NotificationModal";
import { textDate, emptyList, websocketPaths, iconTypeMap, roleName } from "../../../../../constants/componentConstants";
import { translator } from "../../../../../constants/actionTypes";
import { ROUTES } from "../../../../../constants/routesConstants";
import { directLoginStorage } from "../../../../../utils/storageHelper";
import { checkIfObjectNotEmpty, appendParam, localStorageHelper } from "../../../../../utils";
import { Bell } from "../../../../../icons";
import "./NotificationStyle.scss";

const Notification = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isPooled = directLoginStorage.get("isDemandLogin");
  const history = useHistory();
  const dataPerPage = 6;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [notificationList, setNotificationList] = useState(emptyList);
  const [increaseOffsetBy, setIncreaseOffsetBy] = useState(0);
  const [loading, setLoading] = useState(true);
  const iconTitles = [t("common.info"), t("common.warning"), t("common.alert"), t("common.alert"), t("common.warning")];
  const iconClasses = ["ml-5", "", "ml-6", "ml-6", ""];
  const showViewAll = [roleName.superadmin, roleName.csmadmin, roleName.labadmin];
  const role = JSON.parse(localStorageHelper.get("role_name"));
  const isSuperAdmin = showViewAll.includes(role?.[0]);
  const [scrollToloadMore, setToLoadMore] = useState(true);

  const webSocket = useRef(null);

  const { userNotificationList } = useSelector(({ headerNotification }) => headerNotification);
  const { notificationTicket } = useSelector(({ common }) => common);

  const toggleModalHandler = () => {
    setIsModalVisible((prevState) => {
      if (prevState) {
        dispatch(setUserNotificationListData());
        setCurrentPageNumber(1);
        setNotificationList((prevNotifyState) => ({ unread_count: prevNotifyState?.unread_count }));
        setIncreaseOffsetBy(0);
        if (document.getElementById("app-content-id")?.style.overflow) document.getElementById("app-content-id").style.overflow = "auto";
        if (document.getElementById("notify-table-modal-id")?.scrollTop) document.getElementById("notify-table-modal-id").scrollTop = 0;
      } else {
        if (document.getElementById("app-content-id")?.style.overflow) document.getElementById("app-content-id").style.overflow = "hidden";
        setToLoadMore(true);
      }
      return !prevState;
    });
  };

  const { handleRate, rateLabDom } = useRateLab();
  const { handleExtendValidity, extendValidityDom } = useExtendVMValidity({ type: "notification" });

  const linkHandler = (path, id) => {
    toggleModalHandler();
    if (path?.startsWith("/mylabs/rate")) {
      const rateItems = path?.split("/");
      rateItems?.[3] && handleRate({ id: rateItems?.[3] });
    } else if (path?.startsWith("/mylabs/extend")) {
      if (!isPooled) {
        const extendLabItems = path?.split("/");
        extendLabItems?.[3] &&
          extendLabItems?.[4] &&
          extendLabItems?.[5] &&
          handleExtendValidity({ user: extendLabItems?.[3], tenant: extendLabItems?.[4], id: extendLabItems?.[5] }, "notification");
      }
    } else {
      if (!isPooled) {
        history.push({ pathname: path, state: { notifyId: id } });
      }
    }
  };

  const updateStatusHandler = (id, status, markAll) => {
    (!status || markAll) && dispatch(userNotificationUpdateReadStatus(id, { read_status: 1 }, markAll));
    const results = notificationList?.results?.map((item) =>
      item?.id === id || markAll ? { ...cloneDeep(item), read_status: true } : { ...cloneDeep(item) }
    );
    setNotificationList((prevState) => ({
      ...cloneDeep(prevState),
      results: results,
      unread_count: markAll ? 0 : prevState?.unread_count - 1,
    }));
  };

  const loadNotificationsHandler = useCallback(
    (urlParams) => {
      dispatch(getUserNotificationListData(urlParams));
    },
    [dispatch]
  );

  const fetchData = (pageNumber) => {
    let urlParams = appendParam("", "offset", (pageNumber || currentPageNumber - 1) * dataPerPage + increaseOffsetBy);
    urlParams = appendParam(urlParams, "ordering", "-created_at");
    urlParams = appendParam(urlParams, "limit", dataPerPage);
    loadNotificationsHandler(urlParams);
  };

  const fetchMoreData = () => {
    setCurrentPageNumber((prevPage) => {
      fetchData(prevPage);
      return prevPage + 1;
    });
  };

  useEffect(() => {
    if (!notificationTicket) {
      dispatch(getNotificationWebSocketTicket({ path: websocketPaths.notifications }));
    }
    return () => {
      webSocket?.current?.close();
    };
  }, []);

  useEffect(() => {
    if (checkIfObjectNotEmpty(userNotificationList) && userNotificationList?.results) {
      if (isModalVisible) {
        setNotificationList((prevState) => {
          const notificationOldResults = cloneDeep(prevState?.results);
          const notificationNewResults = cloneDeep(userNotificationList?.results) || [];
          return {
            count: userNotificationList?.count,
            results: checkIfObjectNotEmpty(notificationOldResults)
              ? [...notificationOldResults, ...notificationNewResults]
              : [...notificationNewResults],
            unread_count: userNotificationList?.unread_count,
          };
        });
      } else if (userNotificationList?.unread_count) {
        setNotificationList((prevState) => {
          return {
            unread_count: userNotificationList?.unread_count,
          };
        });
      }
    }
    if (document?.getElementsByClassName("notify-table-modal")?.[1])
      document.getElementsByClassName("notify-table-modal")[1].id = "notify-table-modal-id";
    setLoading(false);
  }, [userNotificationList]);

  const webSocketHandler = () => {
    webSocket.current.onmessage = (event) => {
      const notificationData = JSON.parse(event?.data)?.payload;
      if (notificationData) {
        if (isModalVisible) {
          setIncreaseOffsetBy((prevState) => prevState + 1);
          setNotificationList((prevState) => {
            const notificationOldResults = cloneDeep(prevState?.results);
            isSuperAdmin && notificationOldResults?.length === 6 && notificationOldResults?.pop();
            const notificationNewResults = { ...notificationData };
            return {
              count: prevState?.count + 1,
              results: checkIfObjectNotEmpty(notificationOldResults)
                ? [notificationNewResults, ...notificationOldResults]
                : [notificationNewResults],
              unread_count: notificationData?.unread_count,
            };
          });
        } else {
          setNotificationList(() => {
            return {
              unread_count: notificationData?.unread_count,
            };
          });
        }
        dispatch(setUserNotificationListDataWs({ ...notificationData }));
      }
    };

    webSocket.current.onclose = (event) => {
      webSocket.current = null;
      dispatch(getNotificationWebSocketTicket({ path: websocketPaths.notifications }));
    };

    webSocket.current.onerror = (event) => {
      webSocket.current = null;
      dispatch(getNotificationWebSocketTicket({ path: websocketPaths.notifications }));
    };
  };

  useEffect(() => {
    if (notificationTicket) {
      if (!webSocket.current) {
        let userId = localStorageHelper.get("userId");
        webSocket.current = new WebSocket(`${translator.wsUrl}${websocketPaths.notifications}/${userId}/${notificationTicket}`);
      }
      webSocketHandler();
    }
  }, [notificationTicket, isModalVisible]);

  const menus = (
    <>
      <div className="main-nav-header-div" key="main-nav-header-div">
        <Text className="sub-title headingColor">{`${t("common.notifications")}${notificationList?.unread_count ? " - " : ""}${notificationList?.unread_count ? notificationList?.unread_count : ""
          }`}</Text>
        {!loading && notificationList?.count > 0 && notificationList?.unread_count !== 0 && (
          <div onClick={() => updateStatusHandler("", "", true)} className="mark-all-css-nav">
            <Text className="label-bold mark-all-text font-weight-notify">{t("common.markAllRead")}</Text>
          </div>
        )}
      </div>
      <Divider className="mt0 mb0 line-color-notify" />
      {loading && (
        <div className="main-loading-div">
          <Text className="sub-title">{t("common.loading")}</Text>
        </div>
      )}
      {!loading && !notificationList?.count && (
        <div className="main-loading-div">
          <Text className="sub-title">{t("common.noNotificationsFound")}</Text>
        </div>
      )}

      {checkIfObjectNotEmpty(notificationList) && notificationList?.results?.length > 0 && (
        <InfiniteScroll
          dataLength={notificationList?.results?.length}
          next={fetchMoreData}
          hasMore={!isSuperAdmin && notificationList?.count > notificationList?.results?.length}
          loader={<Text className="label-bold notify-loading-text">{t("common.loading")}</Text>}
          scrollableTarget="notify-table-modal-id"
          scrollThreshold={0.4}
          onScroll={() => setToLoadMore(false)}
        >
          {notificationList?.results?.map((item) => {
            const labelcls = item?.read_status ? "body-short-text" : "body-short-text font-weight-notify";
            return (
              <>
                <div
                  key={item?.id}
                  className={`main-nav-div ${isSuperAdmin ? "height-4pt2" : ""} ${item?.read_status ? "read-notifications-bg" : ""} `}
                >
                  <div className="nav-title-div">
                    <Text
                      className={`${item?.read_status ? "label-regular" : "label-regular font-weight-notify"} ${iconClasses[item?.notification_data?.icon]
                        }`}
                    >
                      {iconTitles[item?.notification_data?.icon]}
                    </Text>
                    <div className="created-at-div-notify">
                      {item?.created_at && (
                        <Text className={item?.read_status ? "label-regular" : "label-regular font-weight-notify"}>
                          {moment(item?.created_at).format(textDate)}
                        </Text>
                      )}
                      {!item?.read_status && (
                        <div onClick={() => updateStatusHandler(item?.id, item?.status)} className={"unread-circle-header"}></div>
                      )}
                    </div>
                  </div>

                  <div className="nav-body-div-notify">
                    <div className="notify-icon-div">
                      <div>{iconTypeMap[item?.notification_data?.icon]}</div>
                      <Text
                        className={`notify-body-space ${labelcls} ${item?.notification_data?.icon === "3" ? "critical-text-notify" : ""} `}
                      >
                        <TooltipParagraph> {item?.notification_data?.template_body}</TooltipParagraph>
                      </Text>
                    </div>
                    {(item?.notification_data?.action_link && item?.notification_data?.action_text) && (
                      <div
                        onClick={() => linkHandler(item?.notification_data?.action_link, item?.id)}
                        className="main-header-action pointer-cursor"
                      >
                        <Text
                          className={`${item?.read_status ? "label-regular" : "label-regular font-weight-notify"
                            } mark-all-text title-text-color font-weight-notify`}
                        >
                          {item?.notification_data?.action_text}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
                <Divider className="mt0 mb0 line-color-notify" />
              </>
            );
          })}
        </InfiniteScroll>
      )}
      {!loading && notificationList?.count > 0 && isSuperAdmin && !isPooled && (
        <Link onClick={toggleModalHandler} to={ROUTES.NOTIFICATIONS} className="main-nav-footer-div" key="main-nav-footer-div">
          {<Text className="label-bold mark-all-text font-weight-notify">{t("header.viewAllNotifications")}</Text>}
        </Link>
      )}
      {!loading &&
        scrollToloadMore &&
        notificationList?.results?.length === 6 &&
        notificationList?.count > 6 &&
        (!isSuperAdmin || isPooled) && (
          <Text className="body-short-text font-weight-notify main-nav-footer-div">{t("common.scrollToLoadMore")}</Text>
        )}
    </>
  );

  return (
    <>
      <NotificationModal
        visible={isModalVisible}
        toggleModalHandler={toggleModalHandler}
        closable={false}
        className={`notify-table-modal`}
        modalTitleCls={"table-modal-title"}
        menus={menus}
      >
        <div
          className="header-icon-style notification-icon color-gray"
          onClick={() => {
            setLoading(true);
            fetchData();
          }}
        >
          <Bell />
          {notificationList?.unread_count > 0 && (
            <div className="notification-number">
              <Text className="label-graph notification-count">{`${notificationList?.unread_count > 99 ? t("common.99Plus") : notificationList?.unread_count
                }`}</Text>
            </div>
          )}
        </div>
      </NotificationModal>
      {rateLabDom}
      {extendValidityDom}
    </>
  );
};

export default Notification;
